import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'

import ReviewDetails from '@/components/misc/CompanyProfile/tabs/ReviewsTab/components/ReviewDetails'
import SkeletonTable from '@/components/skeleton/SkeletonTable'
import SkeletonText from '@/components/skeleton/SkeletonText'
import {
  Accordion,
  AccordionChevronDownIcon,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Pagination } from '@/components/ui/pagination'
import { NUM_COMPANY_REVIEWS_PER_PAGE } from '@/lib/constants'
import {
  faBriefcase,
  faLocationDot,
  faUserGroup,
} from '@/lib/fontAwesomePro/light'
import reviewService from '@/lib/services/reviewService'
import { ReviewCollection } from '@/types/responses/review'

interface Props {
  companyId: number
  render?: (props: RenderProps) => React.ReactNode
}

interface RenderProps {
  data: ReviewCollection
  renderReviews: () => React.ReactNode
}

const CompanyReviews: React.FC<Props> = ({ companyId, render }) => {
  const [page, setPage] = useState<number>(1)
  const [activeReviewId, setActiveReviewId] = useState<number | null>(null)

  const reviewsQuery = useQuery({
    queryKey: ['reviews', { companyId, page }],
    queryFn: () =>
      reviewService.getReviewsForCompany(companyId, {
        page,
        pageSize: NUM_COMPANY_REVIEWS_PER_PAGE,
      }),
    refetchOnWindowFocus: false,
  })

  function renderReviewerInfo(faIcon: IconProp, info: string) {
    return (
      <div className="flex items-center lg:col-span-4">
        <FontAwesomeIcon
          icon={faIcon}
          className="text-primary mr-2"
          style={{ fontSize: '16px', width: '20px' }}
        />
        {info}
      </div>
    )
  }

  if (reviewsQuery.isLoading) {
    return <Skeleton />
  }

  const reviews = reviewsQuery.data

  if (reviewsQuery.isError || !reviews) {
    throw new Error(`Failed to fetch reviews for company: ${companyId}`)
  }

  const renderReviews = () => {
    const totalReviewsCount = reviews['hydra:totalItems']

    return (
      <>
        <Accordion
          collapsible
          type="single"
          value={activeReviewId ? activeReviewId.toString() : ''}
          onValueChange={(selectedReviewId) =>
            setActiveReviewId(
              selectedReviewId ? Number(selectedReviewId) : null,
            )
          }
          className="bg-white"
        >
          {reviews['hydra:member'].map((review) => (
            <AccordionItem
              key={review.id}
              value={review.id.toString()}
              className="bg-white p-2"
            >
              <AccordionTrigger asChild>
                <div className="flex items-center gap-2">
                  <div className="grid grow grid-cols-1 gap-4 lg:grid-cols-12">
                    <div className="lg:col-span-4">
                      <div className="font-medium text-black">
                        "{review.title}"
                      </div>
                    </div>

                    <div className="lg:col-span-8">
                      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
                        {renderReviewerInfo(
                          faBriefcase,
                          review.reviewer.title + ', ' + review.reviewer.firm,
                        )}
                        {renderReviewerInfo(
                          faLocationDot,
                          review.reviewer.location,
                        )}
                        {renderReviewerInfo(
                          faUserGroup,
                          review.reviewer.numEmployees,
                        )}
                      </div>
                    </div>
                  </div>

                  <AccordionChevronDownIcon />
                </div>
              </AccordionTrigger>

              <AccordionContent className="py-2">
                {activeReviewId && <ReviewDetails reviewId={activeReviewId} />}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {totalReviewsCount > NUM_COMPANY_REVIEWS_PER_PAGE && (
          <div className="mt-4">
            <Pagination
              activePage={page}
              totalItemsCount={totalReviewsCount}
              numItemsPerPage={NUM_COMPANY_REVIEWS_PER_PAGE}
              onChange={setPage}
            />
          </div>
        )}
      </>
    )
  }

  return render
    ? render({
        data: reviewsQuery.data,
        renderReviews,
      })
    : renderReviews()
}

const Skeleton: React.FC = () => (
  <>
    <SkeletonText className="my-4" length={30} />
    <SkeletonTable rows={7} columns={4} />
  </>
)

export default CompanyReviews
